import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import About from '../components/About';
import WhyChoose from '../components/WhyChoose';
import Services from '../components/Services';
import Projects from '../components/Projects';
import Contact from '../components/Contact';
import { companyInfo } from '../data';

const Home = () => {
  // WhatsApp number with Saudi Arabia country code
  const whatsappNumber = '966535221070';
  const whatsappMessage = 'Hello Al Abeer Diamond, I would like to inquire about your services.';
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <>
      <Helmet>
        <title>Al Abeer Diamond – Building Excellence, Ensuring Safety</title>
        <meta name="description" content={companyInfo.description} />
        <meta name="keywords" content="construction, saudi arabia, building, contractors, al abeer diamond, MEP, manpower supply" />
        <link rel="canonical" href="https://www.alabeerdiamondllc.com" />
      </Helmet>

      <Hero />
      <About />
      <WhyChoose />
      <Services />
      <Projects />
      <Contact />

      {/* WhatsApp Floating Button */}
      <motion.a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          type: 'spring',
          stiffness: 260,
          damping: 20,
          delay: 1.5
        }}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all duration-300 group flex items-center justify-center"
        style={{
          width: '60px',
          height: '60px',
          boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
        }}
        whileHover={{ 
          scale: 1.1,
          boxShadow: '0 8px 30px rgba(37, 211, 102, 0.6)'
        }}
        whileTap={{ scale: 0.9 }}
        aria-label="Chat on WhatsApp"
      >
        {/* WhatsApp Icon */}
        <svg 
          className="w-8 h-8"
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M12 2C6.48 2 2 6.48 2 12C2 14.13 2.74 16.09 4.05 17.62L3.21 21.35C3.08 21.88 3.56 22.32 4.08 22.13L7.72 20.67C9.56 21.52 11.72 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" 
            fill="currentColor"
          />
          <path 
            d="M16.47 14.62C16.1 14.25 15.63 14.08 15.24 13.93C14.85 13.78 14.21 13.62 13.82 14.25C13.43 14.88 12.97 15.32 12.7 15.53C12.43 15.74 12.11 15.78 11.82 15.63C11.53 15.48 10.48 15.08 9.38 14.08C8.28 13.08 7.93 12.27 7.78 11.93C7.63 11.59 7.82 11.3 7.99 11.13C8.16 10.96 8.37 10.71 8.54 10.5C8.71 10.29 8.76 10.12 8.87 9.87C8.98 9.62 8.93 9.41 8.84 9.26C8.75 9.11 8.4 8.45 8.26 8.15C8.13 7.86 8 7.88 7.9 7.88C7.8 7.88 7.68 7.88 7.56 7.88C7.44 7.88 7.24 7.93 7.07 8.11C6.9 8.29 6.38 8.82 6.38 9.88C6.38 10.94 7.04 11.96 7.38 12.4C7.72 12.84 9.55 15.43 12.35 16.73C13.15 17.12 13.76 17.34 14.22 17.5C15.28 17.88 16.26 17.78 17.04 17.56C17.91 17.32 18.74 16.72 18.96 16.03C19.18 15.34 19.18 14.74 19.08 14.58C18.98 14.42 18.74 14.32 18.47 14.18C18.2 14.04 16.9 13.41 16.68 13.31C16.46 13.21 16.29 13.16 16.12 13.42C15.95 13.68 15.45 14.28 15.32 14.45C15.19 14.62 15.06 14.66 14.79 14.52C14.52 14.38 13.71 14.06 12.77 13.24C11.83 12.42 11.44 11.52 11.28 11.18C11.12 10.84 11.26 10.66 11.39 10.52C11.52 10.38 11.68 10.18 11.81 9.99C11.94 9.8 11.99 9.65 12.08 9.44C12.17 9.23 12.12 9.05 12.04 8.91C11.96 8.77 11.58 8.07 11.43 7.75C11.28 7.43 11.13 7.49 11.01 7.49C10.89 7.49 10.75 7.48 10.6 7.48C10.45 7.48 10.19 7.54 9.97 7.78C9.75 8.02 9.05 8.7 9.05 10.08C9.05 11.46 9.91 12.78 10.14 13.1C10.37 13.42 12.05 15.76 14.52 16.84C14.92 17 15.24 17.1 15.5 17.18C15.88 17.3 16.24 17.27 16.53 17.21C16.86 17.14 17.56 16.61 17.72 16.04C17.88 15.47 17.88 14.98 17.82 14.87C17.76 14.76 17.56 14.7 17.36 14.6C17.16 14.5 16.5 14.18 16.47 14.62Z" 
            fill="white"
          />
        </svg>

        {/* Pulse Animation Ring */}
        <span className="absolute inset-0 rounded-full animate-ping bg-green-400 opacity-50" style={{ animationDuration: '2s' }}></span>
        
        {/* Tooltip */}
        <span className="absolute right-full mr-4 bg-gray-800 text-white text-sm font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap hidden sm:block">
          Chat with us on WhatsApp
          <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-800"></span>
        </span>
      </motion.a>
    </>
  );
};

export default Home;